import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, User, X } from 'lucide-react';
import { useAppState } from '../state/AppStateProvider';
import { USER_NAME } from '../configs/configs';
import { SESSION_EVENT, clearSession, hasSession, loginPath } from '../utils/session';
import { Dropdown, cx } from '../ui';
import { useOverlay } from '../ui/hooks';
import Logo from './site/Logo';
import './navBar.css';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'All Projects', to: '/products' },
  { label: 'Free Code', to: '/code' },
  { label: 'Videos', to: '/videos' },
  { label: 'Reviews', to: '/reviews' },
];

// Matches the breakpoint in navBar.css where the links move into the menu sheet.
const DESKTOP_QUERY = '(min-width: 900px)';

const isActivePath = (pathname, to) =>
  to === '/'
    ? pathname === '/'
    : pathname === to || pathname.startsWith(`${to}/`);

const getUserInitials = name => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const NavBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { authState } = useAppState();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const headerRef = useRef(null);

  // Re-read the session on auth state changes and whenever any page starts or ends it.
  useEffect(() => {
    const syncSession = () => {
      if (hasSession()) {
        setIsLoggedIn(true);
        setUserName(localStorage.getItem(USER_NAME) || 'User');
      } else {
        setIsLoggedIn(false);
        setUserName('');
      }
    };
    syncSession();
    window.addEventListener(SESSION_EVENT, syncSession);
    window.addEventListener('storage', syncSession);
    return () => {
      window.removeEventListener(SESSION_EVENT, syncSession);
      window.removeEventListener('storage', syncSession);
    };
  }, [authState]);

  // The bar sits flush on the page at the top and gets its edge once content scrolls under it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const media = window.matchMedia?.(DESKTOP_QUERY);
    if (!media) return undefined;
    const onChange = () => media.matches && setMenuOpen(false);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // Escape closes the sheet, the page behind it stops scrolling, and Tab stays inside the header.
  useOverlay(menuOpen, () => setMenuOpen(false), headerRef);

  const handleLogout = () => {
    clearSession();
    setMenuOpen(false);
    navigate('/');
  };

  const userMenuItems = [
    {
      key: 'profile',
      label: 'Profile',
      icon: <User aria-hidden="true" />,
      onClick: () => navigate('/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: 'Log out',
      icon: <LogOut aria-hidden="true" />,
      onClick: handleLogout,
    },
  ];

  const firstName = userName.split(' ')[0];
  const initials = getUserInitials(userName);

  return (
    <header
      ref={headerRef}
      tabIndex={-1}
      className={cx(
        'pk-nav',
        scrolled && 'is-scrolled',
        menuOpen && 'is-open'
      )}
    >
      <div className="pk-nav__bar">
        <Link to="/" className="pk-nav__logo" aria-label="Projects Kart home">
          <Logo />
        </Link>

        <nav className="pk-nav__links" aria-label="Main">
          {NAV_LINKS.map(link => {
            const active = isActivePath(pathname, link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={cx('pk-nav__link', active && 'is-active')}
                aria-current={active ? 'page' : undefined}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="pk-nav__actions">
          {isLoggedIn ? (
            <Dropdown
              items={userMenuItems}
              placement="bottomRight"
              className="pk-nav__account"
              menuClassName="pk-nav__menu"
            >
              <button
                type="button"
                className="pk-nav__user"
                aria-label={`Account menu for ${userName}`}
              >
                <span className="pk-nav__avatar" aria-hidden="true">
                  {initials}
                </span>
                <span className="pk-nav__user-name">{firstName}</span>
                <ChevronDown className="pk-nav__chevron" aria-hidden="true" />
              </button>
            </Dropdown>
          ) : (
            <Link to={loginPath(pathname)} className="pk-nav__cta">
              Log in
            </Link>
          )}

          <button
            type="button"
            className="pk-nav__toggle"
            aria-expanded={menuOpen}
            aria-controls="pk-nav-sheet"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen(open => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div id="pk-nav-sheet" className="pk-nav__sheet">
          <nav className="pk-nav__sheet-links" aria-label="Mobile">
            {NAV_LINKS.map(link => {
              const active = isActivePath(pathname, link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={cx('pk-nav__sheet-link', active && 'is-active')}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="pk-nav__sheet-foot">
            {isLoggedIn ? (
              <>
                <Link
                  to="/profile"
                  className="pk-nav__sheet-user"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="pk-nav__avatar pk-nav__avatar--lg" aria-hidden="true">
                    {initials}
                  </span>
                  <span className="pk-nav__sheet-user-text">
                    <span className="pk-nav__sheet-user-name">{userName}</span>
                    <span className="pk-nav__sheet-user-hint">View profile</span>
                  </span>
                </Link>
                <button
                  type="button"
                  className="pk-nav__sheet-btn pk-nav__sheet-btn--quiet"
                  onClick={handleLogout}
                >
                  <LogOut aria-hidden="true" />
                  Log out
                </button>
              </>
            ) : (
              <Link
                to={loginPath(pathname)}
                className="pk-nav__sheet-btn"
                onClick={() => setMenuOpen(false)}
              >
                Log in or sign up
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default NavBar;
